// Admin OSS-verification queue (arc 101 slice 07, #68). One read —
// `GET /v1/admin/oss-verifications`, gated `admin:read:orgs` — in the same
// cursor-paginated shape as the other admin registries (`lib/admin.ts`).
//
// The VERDICT is deliberately not here: approve/reject go through
// `POST /v1/admin/oss-verifications/:id/decide` (`sendOverride`,
// `lib/admin-overrides.ts`), on an `admin:accounts` operator credential (#5724).

import { createQuery } from '@tanstack/solid-query';
import type { Page } from './admin';
import { apiFetch } from './api';
import type { OssVerificationStatus } from './ossVerification';
import { SESSION_TOKEN } from './session';

/** Queue filter — `all` drops the status fence; anything else pins one status. */
export type AdminOssFilter = OssVerificationStatus | 'all';

/** One queued application, joined to the account that filed it. */
export type AdminOssRequest = {
  readonly id: string;
  readonly account_id: string;
  readonly account_login: string;
  readonly repo_full_name: string;
  readonly evidence: string;
  readonly status: OssVerificationStatus;
  readonly reviewer_note: string | null;
  /** The maintainer who filed it; null for an application with no user session. */
  readonly submitted_by_login: string | null;
  readonly submitted_at_iso: string;
  readonly decided_at_iso: string | null;
};

export const ADMIN_OSS_KEY = 'admin_oss_verifications';

/** One page of the queue, newest-first. Idle (no fetch) with no session. */
export function useAdminOssVerifications(
  status: () => AdminOssFilter,
  cursor: () => string | null,
  perPage = 30,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<AdminOssRequest>>(() => ({
    queryKey: [ADMIN_OSS_KEY, token, status(), cursor(), perPage],
    enabled: token !== null,
    queryFn: () =>
      apiFetch<Page<AdminOssRequest>>(
        `/v1/admin/oss-verifications?${queueParams(status(), cursor(), perPage)}`,
        token,
      ),
  }));
}

/**
 * Build the query string. `status` is ALWAYS sent, unlike the user/org
 * registries where an omitted term means "no filter": this route defaults to
 * `pending`, so dropping it would silently hide the decided rows the filter just
 * asked for. That includes `all` — here it is a real value ("drop the fence"),
 * not the absence of one.
 *
 * It is the shared grammar's `?filter=status:<value>`, not a flat `?status=`:
 * the API refuses that spelling by name, so one page builder serves every
 * collection on this surface. The page size is `per_page`, the one word every
 * admin collection answers to; the `?limit` alias is gone and the route 400s
 * it by name.
 */
function queueParams(status: AdminOssFilter, cursor: string | null, perPage: number): string {
  const params = new URLSearchParams({
    filter: `status:${status}`,
    per_page: String(perPage),
  });
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}
